import { useState } from "react";
import { Apis } from "../../lib/Apis";

const getApiError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

/** Unwrap `{ success, statusCode, data }` response model. */
const unwrapResponseBody = (body) => {
  if (body == null) return null;
  if (body?.success === false) {
    throw new Error(body?.message || "Request failed.");
  }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    ("success" in body || "statusCode" in body)
  ) {
    return body.data;
  }
  return body;
};

const unwrapListPayload = (body) => {
  const payload = unwrapResponseBody(body) ?? body ?? {};
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  const pagination = payload?.pagination ?? body?.pagination ?? null;
  const totalCount =
    pagination?.totalRecords ??
    pagination?.totalCount ??
    pagination?.totalItems ??
    items.length;
  return { items, pagination, totalCount, payload };
};

// --- Contracts ---
export const useContracts = () => {
  const createContract = async (contractData) => {
    try {
      const response = await Apis.postContract(contractData);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error creating contract:", error);
      throw error;
    }
  };

  const getContracts = async (offset = 0, limit = 10, params = {}) => {
    try {
      const response = await Apis.getContracts(offset, limit, params);
      const { items, pagination, totalCount } = unwrapListPayload(
        response?.data,
      );
      return { data: items, pagination, totalCount };
    } catch (error) {
      console.error("Error fetching contracts:", error);
      throw error;
    }
  };

  const getContractById = async (id) => {
    try {
      const response = await Apis.getContractById(id);
      return unwrapResponseBody(response?.data);
    } catch (error) {
      console.error("Error fetching contract:", error);
      throw error;
    }
  };

  const deleteContract = async (id) => {
    try {
      const response = await Apis.deleteContract(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting contract:", error);
      throw error;
    }
  };

  const updateContract = async (id, data) => {
    try {
      const response = await Apis.updateContract(id, data);
      return unwrapResponseBody(response?.data);
    } catch (error) {
      console.error("Error updating contract:", error);
      throw error;
    }
  };

  const getUserContracts = async (params) => {
    try {
      const response = await Apis.getUserContracts(params);
      // user-contracts may or may not use response model yet
      const { items, pagination, totalCount, payload } = unwrapListPayload(
        response?.data,
      );
      if (items.length || pagination) {
        return { data: items, pagination, totalCount, ...payload };
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching user contracts:", error);
      throw error;
    }
  };

  const getAllContractCategories = async () => {
    try {
      const response = await Apis.getContractCategories(0, 1000);
      const { items } = unwrapListPayload(response?.data);
      return items;
    } catch (error) {
      console.error("Error fetching contract categories:", error);
      throw error;
    }
  };

  return {
    createContract,
    getContracts,
    deleteContract,
    getContractById,
    updateContract,
    getUserContracts,
    getAllContractCategories,
  };
};

// --- Contract categories ---
export const useContractCategory = () => {
  const [loading, setLoading] = useState(false);

  const addContractCategory = async (categoryData) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("title", categoryData.title);
      if (categoryData.categoryTitle_ur) {
        formData.append("categoryTitle_ur", categoryData.categoryTitle_ur);
      }
      if (categoryData.description) {
        formData.append("description", categoryData.description);
      }
      if (categoryData.description_ur) {
        formData.append("description_ur", categoryData.description_ur);
      }
      if (categoryData.custom_url) {
        formData.append("custom_url", categoryData.custom_url);
      }

      if (categoryData.image instanceof File) {
        formData.append("image", categoryData.image);
      }
      if (categoryData.image_alt != null) {
        formData.append("image_alt", categoryData.image_alt);
      }
      if (categoryData.image_title != null) {
        formData.append("image_title", categoryData.image_title);
      }

      const response = await Apis.addContractCategory(formData);
      if (response?.status === 201 || response?.data?.success) {
        return unwrapResponseBody(response?.data) ?? response?.data;
      }
      throw new Error(
        response?.data?.message ||
          response?.data?.error ||
          "Failed to add category.",
      );
    } catch (error) {
      throw new Error(getApiError(error, "Error adding category."));
    } finally {
      setLoading(false);
    }
  };

  const editContractCategory = async (id, categoryData) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("title", categoryData.title);
      if (categoryData.categoryTitle_ur) {
        formData.append("categoryTitle_ur", categoryData.categoryTitle_ur);
      }
      if (categoryData.description !== undefined) {
        formData.append("description", categoryData.description);
      }
      if (categoryData.description_ur !== undefined) {
        formData.append("description_ur", categoryData.description_ur);
      }
      if (categoryData.custom_url) {
        formData.append("custom_url", categoryData.custom_url);
      }

      if (categoryData.image instanceof File) {
        formData.append("image", categoryData.image);
      }
      if (categoryData.image_alt != null) {
        formData.append("image_alt", categoryData.image_alt);
      }
      if (categoryData.image_title != null) {
        formData.append("image_title", categoryData.image_title);
      }

      const response = await Apis.updateContractCategory(id, formData);
      if (response?.status === 200 || response?.data?.success) {
        return unwrapResponseBody(response?.data) ?? response?.data;
      }
      throw new Error(
        response?.data?.message ||
          response?.data?.error ||
          "Failed to edit category.",
      );
    } catch (error) {
      throw new Error(getApiError(error, "Error editing category."));
    } finally {
      setLoading(false);
    }
  };

  const getContractCategories = async (
    setCategories,
    setTotal,
    offset = 0,
    limit = 10,
    search = "",
  ) => {
    setLoading(true);
    try {
      const params = {};
      if (search) {
        params.search = search;
      }

      const response = await Apis.getContractCategories(offset, limit, params);

      if (response?.status === 200) {
        const { items, pagination, totalCount } = unwrapListPayload(
          response?.data,
        );
        setCategories(items);
        setTotal(totalCount);
        return { data: items, pagination, totalCount };
      }
      throw new Error(
        response?.data?.message || "Failed to fetch categories.",
      );
    } catch (error) {
      console.error("Error fetching categories:", error);
      throw new Error(getApiError(error, "Error fetching categories."));
    } finally {
      setLoading(false);
    }
  };

  const deleteContractCategory = async (id) => {
    try {
      setLoading(true);
      const response = await Apis.deleteContractCategory(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      throw new Error(getApiError(error, "Error deleting category."));
    } finally {
      setLoading(false);
    }
  };

  return {
    addContractCategory,
    editContractCategory,
    loading,
    getContractCategories,
    deleteContractCategory,
  };
};
